import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  FaGift,
  FaUserPlus,
  FaMoneyBill,
  FaUsers,
  FaSearch,
  FaCopy,
  FaCheckCircle,
  FaExternalLinkAlt,
  FaSpinner,
  FaSort,
} from 'react-icons/fa';
import api from '../../services/api';
import './styles/ReferralPageDashboard.css';

export default function ReferralPageDashboard() {
  // ============================================
  // STATE
  // ============================================

  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [referralResult, setReferralResult] = useState(null);
  const [overview, setOverview] = useState(null);
  const [payments, setPayments] = useState([]);
  const [referralUsers, setReferralUsers] = useState([]);

  // Loading states
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [loadingReferralUsers, setLoadingReferralUsers] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);

  // Search & Filter states
  const [paymentSearch, setPaymentSearch] = useState('');
  const [referralUserSearch, setReferralUserSearch] = useState('');

  // Sort states
  const [sortOverview, setSortOverview] = useState('bonus');

  // Utility
  const [copiedLink, setCopiedLink] = useState(null);
  const [activeTab, setActiveTab] = useState('generator');

  // ============================================
  // FETCH FUNCTIONS
  // ============================================

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await api.get('/admin/users', {
        params: { page: 1, pageSize: 100 },
      });
      setUsers(response.data.items || []);
    } catch (error) {
      console.error('Fetch users error:', error);
      toast.error('❌ İstifadəçilər yüklənmədi');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchOverview = async () => {
    setLoadingOverview(true);
    try {
      const response = await api.get('/admin/referral/overview');
      setOverview(response.data);
    } catch (error) {
      console.error('Fetch overview error:', error);
      toast.error('❌ Referral overview alınmadı');
    } finally {
      setLoadingOverview(false);
    }
  };

  const fetchPayments = async () => {
    setLoadingPayments(true);
    try {
      const response = await api.get('/admin/referral/payments');
      setPayments(response.data);
    } catch (error) {
      console.error('Fetch payments error:', error);
      toast.error('❌ Ödənişlər alınmadı');
    } finally {
      setLoadingPayments(false);
    }
  };

  const fetchReferralUsers = async () => {
    setLoadingReferralUsers(true);
    try {
      const response = await api.get('/admin/referral/users');
      setReferralUsers(response.data);
    } catch (error) {
      console.error('Fetch referral users error:', error);
      toast.error('❌ Referral istifadəçiləri alınmadı');
    } finally {
      setLoadingReferralUsers(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchUsers();
    fetchOverview();
    fetchPayments();
    fetchReferralUsers();
  }, []);

  // ============================================
  // ACTION FUNCTIONS
  // ============================================

  const handleGenerateCode = async (e) => {
    e.preventDefault();
    
    if (!selectedUserId.trim()) {
      toast.warning('⚠️ Lütfen bir istifadəçi seçin');
      return;
    }

    setGeneratingCode(true);
    try {
      const response = await api.post(`/admin/referral/create/${selectedUserId}`);
      setReferralResult(response.data);
      toast.success('✅ Referral kod uğurla yaradıldı!');
    } catch (error) {
      console.error('Generate code error:', error);
      const errorMsg = error.response?.data?.message || 'Kod yaradıla bilmədi';
      toast.error(`❌ ${errorMsg}`);
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleCopyToClipboard = async (text, linkId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLink(linkId);
      toast.success('📋 Kopyalandı!');
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (error) {
      console.error('Copy error:', error);
      toast.error('❌ Kopyalanmadı');
    }
  };

  const handleOpenLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // ============================================
  // FILTER & SORT FUNCTIONS
  // ============================================

  const filteredPayments = payments.filter((p) => {
    const searchLower = paymentSearch.toLowerCase();
    return (
      p.payingUser.toLowerCase().includes(searchLower) ||
      p.payingUserEmail.toLowerCase().includes(searchLower) ||
      (p.referrer?.fullName.toLowerCase().includes(searchLower) ?? false)
    );
  });

  const sortedPayments = [...filteredPayments].sort((a, b) => {
    return new Date(b.paidAt).getTime() - new Date(a.paidAt).getTime();
  });

  const filteredReferralUsers = referralUsers.filter((u) => {
    const searchLower = referralUserSearch.toLowerCase();
    return (
      u.fullName.toLowerCase().includes(searchLower) ||
      u.email.toLowerCase().includes(searchLower)
    );
  });

  const sortedOverview = overview
    ? {
        ...overview,
        topReferrers: [...overview.topReferrers].sort((a, b) => {
          if (sortOverview === 'bonus') {
            return b.bonus - a.bonus;
          }
          return 0;
        }),
      }
    : null;

  // ============================================
  // RENDER SECTIONS
  // ============================================

  const renderCodeGeneratorSection = () => (
    <div className="rp-main-card">
      <div className="rp-card-header">
        <h3 className="rp-card-title">
          <FaUserPlus /> Referral Kodu Yarat
        </h3>
      </div>

      <form className="rp-form" onSubmit={handleGenerateCode}>
        <div className="rp-form-group">
          <label className="rp-form-label">İstifadəçi Seçin *</label>
          <select
            className="rp-form-select"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            disabled={loadingUsers || generatingCode}
          >
            <option value="">-- İstifadəçi seçin --</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.fullName} ({user.email})
              </option>
            ))}
          </select>
        </div>

        <div className="rp-form-actions">
          <button
            type="submit"
            className="rp-button-primary"
            disabled={generatingCode || !selectedUserId}
          >
            {generatingCode ? (
              <>
                <FaSpinner className="rp-spinner" /> Yaradılır...
              </>
            ) : (
              <>
                <FaGift /> Kod Yarat
              </>
            )}
          </button>
        </div>
      </form>

      {/* Result Section */}
      {referralResult && (
        <div className="rp-result-section">
          <div className="rp-result-item">
            <span className="rp-result-label">Kod:</span>
            <code className="rp-result-value">{referralResult.referralCode}</code>
            <button
              className="rp-button-copy"
              onClick={() => handleCopyToClipboard(referralResult.referralCode, 'code')}
            >
              {copiedLink === 'code' ? (
                <>
                  <FaCheckCircle /> Kopyalandı
                </>
              ) : (
                <>
                  <FaCopy /> Kopyala
                </>
              )}
            </button>
          </div>

          <div className="rp-result-item">
            <span className="rp-result-label">Link:</span>
            <code className="rp-result-value">{referralResult.referralLink}</code>
            <div className="rp-button-group">
              <button
                className="rp-button-copy"
                onClick={() => handleCopyToClipboard(referralResult.referralLink, 'link')}
              >
                {copiedLink === 'link' ? (
                  <>
                    <FaCheckCircle /> Kopyalandı
                  </>
                ) : (
                  <>
                    <FaCopy /> Kopyala
                  </>
                )}
              </button>
              <button
                className="rp-button-open"
                onClick={() => handleOpenLink(referralResult.referralLink)}
              >
                <FaExternalLinkAlt /> Aç
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderOverviewSection = () => (
    <div className="rp-main-card">
      <div className="rp-card-header">
        <h3 className="rp-card-title">
          <FaGift /> Ümumi Statistikalar
        </h3>
        <button
          className="rp-sort-button"
          onClick={() => setSortOverview(sortOverview === 'bonus' ? 'referrals' : 'bonus')}
        >
          <FaSort /> {sortOverview === 'bonus' ? 'Bonus' : 'Referrals'}
        </button>
      </div>

      {loadingOverview ? (
        <div className="rp-loading">
          <div className="rp-spinner-icon" />
          <span>Yüklənir...</span>
        </div>
      ) : sortedOverview ? (
        <>
          {/* Stats Grid */}
          <div className="rp-stats-grid">
            <div className="rp-stat-card">
              <div className="rp-stat-icon">💰</div>
              <div className="rp-stat-content">
                <p className="rp-stat-label">Toplam Bonus</p>
                <p className="rp-stat-value">
                  {sortedOverview.totalBonus.toFixed(2)} ₼
                </p>
              </div>
            </div>

            <div className="rp-stat-card">
              <div className="rp-stat-icon">👥</div>
              <div className="rp-stat-content">
                <p className="rp-stat-label">Bonus Alanlar</p>
                <p className="rp-stat-value">{sortedOverview.totalReferrers}</p>
              </div>
            </div>

            <div className="rp-stat-card">
              <div className="rp-stat-icon">🔗</div>
              <div className="rp-stat-content">
                <p className="rp-stat-label">Dəvətlə Gələnlər</p>
                <p className="rp-stat-value">{sortedOverview.totalReferredUsers}</p>
              </div>
            </div>
          </div>

          {/* Top Referrers Table */}
          <div className="rp-table-section">
            <h4 className="rp-table-title">🏆 Top 10 Referral</h4>
            <div className="rp-table-wrapper">
              <table className="rp-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Ad Soyad</th>
                    <th>Email</th>
                    <th>Bonus</th>
                    <th>Əməliyyat</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedOverview.topReferrers.map((referrer, idx) => (
                    <tr key={referrer.id}>
                      <td className="rp-table-index">{idx + 1}</td>
                      <td className="rp-table-name">{referrer.fullName}</td>
                      <td className="rp-table-email">{referrer.email}</td>
                      <td className="rp-table-bonus">
                        <strong>{referrer.bonus.toFixed(2)} ₼</strong>
                      </td>
                      <td className="rp-table-actions">
                        <button
                          className="rp-button-small-open"
                          onClick={() => handleOpenLink(referrer.referralLink)}
                          title="Linki aç"
                        >
                          <FaExternalLinkAlt />
                        </button>
                        <button
                          className="rp-button-small-copy"
                          onClick={() =>
                            handleCopyToClipboard(
                              referrer.referralLink,
                              `ref-${referrer.id}`
                            )
                          }
                          title="Linki kopyala"
                        >
                          {copiedLink === `ref-${referrer.id}` ? (
                            <FaCheckCircle />
                          ) : (
                            <FaCopy />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="rp-empty-state">
          <div className="rp-empty-icon">📊</div>
          <p className="rp-empty-text">Statistika yoxdur</p>
        </div>
      )}
    </div>
  );

  const renderPaymentsSection = () => (
    <div className="rp-main-card">
      <div className="rp-card-header">
        <h3 className="rp-card-title">
          <FaMoneyBill /> Referral Ödənişləri
        </h3>
        <div className="rp-search-box">
          <FaSearch className="rp-search-icon" />
          <input
            type="text"
            placeholder="Axtar..."
            value={paymentSearch}
            onChange={(e) => setPaymentSearch(e.target.value)}
            className="rp-search-input"
          />
        </div>
      </div>

      {loadingPayments ? (
        <div className="rp-loading">
          <div className="rp-spinner-icon" />
          <span>Yüklənir...</span>
        </div>
      ) : sortedPayments.length > 0 ? (
        <div className="rp-table-wrapper">
          <table className="rp-table">
            <thead>
              <tr>
                <th>Profil ID</th>
                <th>Ödəyən</th>
                <th>Referral Kimə Getdi</th>
                <th>Tarix</th>
              </tr>
            </thead>
            <tbody>
              {sortedPayments.map((payment, idx) => (
                <tr key={idx}>
                  <td className="rp-table-id">#{payment.profileId}</td>
                  <td>
                    <div className="rp-table-user-info">
                      <div className="rp-table-user-name">{payment.payingUser}</div>
                      <div className="rp-table-user-email">{payment.payingUserEmail}</div>
                    </div>
                  </td>
                  <td>
                    {payment.referrer ? (
                      <div className="rp-table-user-info">
                        <div className="rp-table-user-name">
                          {payment.referrer.fullName}
                        </div>
                        <div className="rp-table-user-email">
                          {payment.referrer.email}
                        </div>
                      </div>
                    ) : (
                      <span className="rp-table-null">—</span>
                    )}
                  </td>
                  <td className="rp-table-date">
                    {new Date(payment.paidAt).toLocaleDateString('az-AZ', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rp-empty-state">
          <div className="rp-empty-icon">💸</div>
          <p className="rp-empty-text">Ödəniş məlumatı yoxdur</p>
        </div>
      )}
    </div>
  );

  const renderUsersSection = () => (
    <div className="rp-main-card">
      <div className="rp-card-header">
        <h3 className="rp-card-title">
          <FaUsers /> İstifadəçilər və Referral Əlaqələri
        </h3>
        <div className="rp-search-box">
          <FaSearch className="rp-search-icon" />
          <input
            type="text"
            placeholder="Axtar..."
            value={referralUserSearch}
            onChange={(e) => setReferralUserSearch(e.target.value)}
            className="rp-search-input"
          />
        </div>
      </div>

      {loadingReferralUsers ? (
        <div className="rp-loading">
          <div className="rp-spinner-icon" />
          <span>Yüklənir...</span>
        </div>
      ) : filteredReferralUsers.length > 0 ? (
        <div className="rp-table-wrapper">
          <table className="rp-table">
            <thead>
              <tr>
                <th>İstifadəçi</th>
                <th>Dəvət Edən</th>
                <th>Bonus</th>
                <th>Ödəniş</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredReferralUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="rp-table-user-info">
                      <div className="rp-table-user-name">{user.fullName}</div>
                      <div className="rp-table-user-email">{user.email}</div>
                    </div>
                  </td>
                  <td>
                    {user.referredBy ? (
                      <div className="rp-table-user-info">
                        <div className="rp-table-user-name">
                          {user.referredBy.fullName}
                        </div>
                        <div className="rp-table-user-email">
                          {user.referredBy.email}
                        </div>
                      </div>
                    ) : (
                      <span className="rp-table-null">—</span>
                    )}
                  </td>
                  <td className="rp-table-bonus">
                    <strong>{user.referralBonus.toFixed(2)} ₼</strong>
                  </td>
                  <td className="rp-table-count">{user.paymentCount}</td>
                  <td>
                    {user.hasPaid ? (
                      <span className="rp-badge-success">
                        <FaCheckCircle /> Ödəniş Etdi
                      </span>
                    ) : (
                      <span className="rp-badge-pending">⏳ Gözləyir</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rp-empty-state">
          <div className="rp-empty-icon">👥</div>
          <p className="rp-empty-text">İstifadəçi məlumatı yoxdur</p>
        </div>
      )}
    </div>
  );

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <div className="rp-container">
      {/* Header */}
      <div className="rp-header">
        <div className="rp-header-content">
          <h1 className="rp-title">
            <FaGift /> Referral Sistemi
          </h1>
          <p className="rp-subtitle">
            Referral proqramının bütün məlumatlarını idarə edin
          </p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="rp-quick-stats">
        <div className="rp-quick-stat-card">
          <div className="rp-quick-stat-icon">👥</div>
          <div>
            <p className="rp-quick-stat-label">Toplam İstifadəçi</p>
            <p className="rp-quick-stat-value">{users.length}</p>
          </div>
        </div>

        <div className="rp-quick-stat-card">
          <div className="rp-quick-stat-icon">💰</div>
          <div>
            <p className="rp-quick-stat-label">Toplam Bonus</p>
            <p className="rp-quick-stat-value">
              {overview?.totalBonus.toFixed(2) || '0'} ₼
            </p>
          </div>
        </div>

        <div className="rp-quick-stat-card">
          <div className="rp-quick-stat-icon">🔗</div>
          <div>
            <p className="rp-quick-stat-label">Referral Sayı</p>
            <p className="rp-quick-stat-value">
              {overview?.totalReferredUsers || '0'}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="rp-tabs">
        <button
          className={`rp-tab-button ${activeTab === 'generator' ? 'rp-tab-button-active' : ''}`}
          onClick={() => setActiveTab('generator')}
        >
          <FaUserPlus /> Kod Yaratma
        </button>
        <button
          className={`rp-tab-button ${activeTab === 'overview' ? 'rp-tab-button-active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <FaGift /> Statistika
        </button>
        <button
          className={`rp-tab-button ${activeTab === 'payments' ? 'rp-tab-button-active' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          <FaMoneyBill /> Ödənişlər ({payments.length})
        </button>
        <button
          className={`rp-tab-button ${activeTab === 'users' ? 'rp-tab-button-active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <FaUsers /> İstifadəçilər ({referralUsers.length})
        </button>
      </div>

      {/* Content */}
      <div className="rp-content">
        {activeTab === 'generator' && renderCodeGeneratorSection()}
        {activeTab === 'overview' && renderOverviewSection()}
        {activeTab === 'payments' && renderPaymentsSection()}
        {activeTab === 'users' && renderUsersSection()}
      </div>
    </div>
  );
}